/**
 *  CENTRALIZED API SERVICE
 * ========================
 * Main service to communicate with the Express.js backend
 * Handles HTTP requests, JWT authentication, and error management
 * 
 */

/// <reference types="vite/client" />
import type { 
  ApiError,
  ApiResponse
} from '../../../types';
import apiErrorHandler from '../../../services/apiErrorHandler';

// ================================
// INTERFACES AND TYPES
// ================================

interface RequestConfig {
  includeAuth?: boolean;
  timeout?: number;
  retries?: number;
  onProgress?: (progress: ProgressEvent) => void;
  // Client-side caching & de-duplication
  useCache?: boolean;
  cacheTtlMs?: number;
  dedupe?: boolean;
}

interface ConnectivityResult {
  api: boolean;
  auth: boolean;
  database: boolean;
  storage: boolean;
  timestamp: string;
}

interface ServiceInfo {
  apiUrl: string;
  hasLocalToken: boolean;
  isLocalTokenValid: boolean;
  userInfo: any | null;
  timestamp: string;
  version: string;
}

// ================================
// MAIN CLASS APISERVICE
// ================================

export class ApiService {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;
  private readonly version = '3.0.0';
  private readonly timeout = 30000; // 30 seconds
  // Lightweight in-memory response cache (GET only)
  private responseCache = new Map<string, { expireAt: number; value: ApiResponse<any> }>();
  // In-flight GET requests for request coalescing
  private inflight = new Map<string, Promise<ApiResponse<any>>>();

  // 🚨 CIRCUIT BREAKER D'URGENCE - Protection contre les boucles infinies
  private emergencyBreaker = {
    isActive: false,
    activatedAt: 0,
    duration: 30000, // 30 secondes d'arrêt total
  };

  // Rate limiter d'urgence pour éviter les boucles infinies
  private requestCache = new Map<string, number>();
  private readonly minRequestInterval = 2000; // 2 secondes minimum entre requêtes identiques

  constructor(baseURL: string = import.meta.env.VITE_API_URL || '/api') {
    this.baseURL = baseURL.replace(/\/$/, ''); // Remove trailing slash
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Client-Version': this.version,
    };
    
    this.logEnvironmentInfo();
  }

  // ================================
  // CACHE INVALIDATION HELPERS
  // ================================

  /**
   * Invalidate a cached GET response for a given endpoint.
   * Example: invalidateGetCache('/settings/features.services')
   */
  public invalidateGetCache(endpoint: string): void {
    try {
      const url = `${this.baseURL}${endpoint}`;
      const cacheKey = `GET:${url}`;
      this.responseCache.delete(cacheKey);
      this.inflight.delete(cacheKey);
    } catch {
      // no-op
    }
  }

  /**
   * Invalidate a list of GET endpoints at once.
   */
  public invalidateGetCaches(endpoints: string[]): void {
    endpoints.forEach((e) => this.invalidateGetCache(e));
  }

  // ================================
  // PRIVATE METHODS - AUTHENTICATION
  // ================================

  /**
   *  Get the local authentication token
   */
  private getAuthToken(): string | null {
  // Extended search for various possible keys used across the app
    const candidates = [
  // LocalStorage (priority)
      localStorage.getItem('auth_token'),
      localStorage.getItem('portfolio_auth_token'),
      localStorage.getItem('portfolio_token'),
      localStorage.getItem('token'),
      // SessionStorage (fallback)
      sessionStorage.getItem('auth_token'),
      sessionStorage.getItem('portfolio_token'),
    ];

    for (const t of candidates) {
      if (t && typeof t === 'string' && t.trim().length > 0) return t;
    }
    return null;
  }

  /**
   *  Prepare headers with authentication
   */
  private getHeaders(config: RequestConfig = {}): Record<string, string> {
    const headers: Record<string, string> = { ...this.defaultHeaders };
    
    if (config.includeAuth !== false) {
      const token = this.getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    return headers;
  }

  /**
   *  Prepare headers for upload (without Content-Type)
   */
  private getUploadHeaders(config: RequestConfig = {}): Record<string, string> {
    const headers: Record<string, string> = {
      'Accept': 'application/json',
      'X-Client-Version': this.version,
    };
    
    if (config.includeAuth !== false) {
      const token = this.getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    return headers;
  }

/**
 *  Process HTTP responses with robust error handling
 */
private async handleResponse<T>(response: Response, endpoint: string): Promise<ApiResponse<T>> {
  const contentType = response.headers.get('content-type') || '';
  let responseData: any;

  try {
    if (contentType.includes('application/json')) {
      responseData = await response.json();
    } else if (contentType.includes('text/')) {
      const textData = await response.text();
      responseData = { message: textData, rawText: textData };
    } else {
      responseData = { message: 'Réponse non-JSON reçue', contentType };
    }
  } catch (parseError) {
    responseData = { 
      message: 'Erreur de parsing de la réponse',
      parseError: parseError instanceof Error ? parseError.message : 'Erreur inconnue'
    };
  }

  // HTTP error handling
  if (!response.ok) {
  // Normalize error for ErrorHandler (with status & name to avoid default /500)
    const apiError = {
      name: 'ApiError',
      message:
        responseData?.error ||
        responseData?.message ||
        `Erreur HTTP ${response.status}: ${response.statusText}`,
      status: response.status,
      code: responseData?.code || `HTTP_${response.status}`,
      details: {
        endpoint,
        method: 'Non spécifié',
        timestamp: new Date().toISOString(),
        responseData,
      },
    } as const;

    try {
      apiErrorHandler.handleError(apiError as any, {
        autoRedirect: true,
        showNotification: false,
        context: `apiService: ${endpoint}`,
      });
    } catch {}
    throw apiError as any;
  }

  // Normalize response
  const normalizedResponse: ApiResponse<T> = {
    success: responseData.success !== false,
    data: responseData.data || responseData,
    message: responseData.message,
    error: responseData.error,
    metadata: {
      status: response.status,
      endpoint,
      timestamp: new Date().toISOString(),
      ...responseData.metadata
    }
  };

  return normalizedResponse;
}

  /**
   *  Create a request with timeout and abort controller
   */
  private async createRequest(
    url: string, 
    options: RequestInit,
    config: RequestConfig = {}
  ): Promise<Response> {
    // ✅ CIRCUIT BREAKER - Protection d'urgence
    const now = Date.now();
    if (this.emergencyBreaker.isActive) {
      if (now - this.emergencyBreaker.activatedAt < this.emergencyBreaker.duration) {
        console.error(`🚨 CIRCUIT BREAKER ACTIF: API bloquée pendant ${Math.ceil((this.emergencyBreaker.duration - (now - this.emergencyBreaker.activatedAt)) / 1000)}s`);
        throw new Error('Circuit breaker active - API temporarily disabled');
      } else {
        this.emergencyBreaker.isActive = false;
        console.log('✅ Circuit breaker désactivé - API restaurée');
      }
    }

    // ✅ PROTECTION URGENTE - Rate limiting côté client (uniquement pour GET/HEAD)
    const method = (options.method || 'GET').toUpperCase();
    if (method === 'GET' || method === 'HEAD') {
      const requestKey = `${method}:${url}`;
      const lastRequest = this.requestCache.get(requestKey);
      if (lastRequest && (now - lastRequest) < this.minRequestInterval) {
        console.warn(`🚨 RATE LIMIT: Requête ${requestKey} bloquée (trop récente)`);
        // Activer le circuit breaker si trop de requêtes répétées
        if (!this.emergencyBreaker.isActive) {
          this.emergencyBreaker.isActive = true;
          this.emergencyBreaker.activatedAt = now;
          console.error('🔥 ACTIVATION CIRCUIT BREAKER - Arrêt total des API pendant 30s');
        }
        throw new Error(`Rate limit exceeded for ${requestKey}`);
      }
      this.requestCache.set(requestKey, now);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), config.timeout || this.timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(`Timeout: La requête vers ${url} a pris trop de temps`);
      }
      
      throw error;
    }
  }

  // ================================
  // PUBLIC HTTP METHODS
  // ================================

  /**
   *  Enhanced GET request
   */
  async get<T>(endpoint: string, includeAuth = true, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    const cacheKey = `GET:${url}`;
    const useCache = config.useCache === true;
    const ttl = typeof config.cacheTtlMs === 'number' ? Math.max(0, config.cacheTtlMs) : 0;
    const dedupe = config.dedupe !== false; // enabled by default

    try {
      // Serve from cache if valid
      if (useCache) {
        const cached = this.responseCache.get(cacheKey);
        if (cached && cached.expireAt > Date.now()) {
          return cached.value as ApiResponse<T>;
        }
      }

      // Coalesce concurrent identical GETs
      if (dedupe && this.inflight.has(cacheKey)) {
        return (await this.inflight.get(cacheKey)!) as ApiResponse<T>;
      }

      const promise = (async () => {
      const response = await this.createRequest(url, {
        method: 'GET',
        headers: this.getHeaders({ includeAuth, ...config }),
      }, config);

      const result = await this.handleResponse<T>(response, endpoint);
        // Put into cache if requested
        if (useCache && ttl > 0) {
          this.responseCache.set(cacheKey, { expireAt: Date.now() + ttl, value: result });
        }
        return result;
      })();

      if (dedupe) this.inflight.set(cacheKey, promise as Promise<ApiResponse<any>>);

      const finalResult = await promise;
      if (dedupe) this.inflight.delete(cacheKey);
      return finalResult;
    } catch (error) {
      if (dedupe) this.inflight.delete(cacheKey);
      throw error;
    }
  }

  /**
   *  Enhanced POST request
   */
  async post<T>(endpoint: string, body?: any, includeAuth = true, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    try {
      const response = await this.createRequest(url, {
        method: 'POST',
        headers: this.getHeaders({ includeAuth, ...config }),
        body: body ? JSON.stringify(body) : undefined,
      }, config);

      const result = await this.handleResponse<T>(response, endpoint);
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   *  Enhanced PUT request
   */
  async put<T>(endpoint: string, body?: any, includeAuth = true, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    try {
      const response = await this.createRequest(url, {
        method: 'PUT',
        headers: this.getHeaders({ includeAuth, ...config }),
        body: body ? JSON.stringify(body) : undefined,
      }, config);

      const result = await this.handleResponse<T>(response, endpoint);
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   *  Enhanced PATCH request
   */
  async patch<T>(endpoint: string, body?: any, includeAuth = true, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    try {
      const response = await this.createRequest(url, {
        method: 'PATCH',
        headers: this.getHeaders({ includeAuth, ...config }),
        body: body ? JSON.stringify(body) : undefined,
      }, config);

      const result = await this.handleResponse<T>(response, endpoint);
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   *  Enhanced DELETE request
   */
  async delete<T>(endpoint: string, includeAuth = true, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    try {
      const response = await this.createRequest(url, {
        method: 'DELETE',
        headers: this.getHeaders({ includeAuth, ...config }),
      }, config);

      const result = await this.handleResponse<T>(response, endpoint);
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   *  File upload with progress and robust error handling
   */
  async upload<T>(endpoint: string, formData: FormData, includeAuth = true, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    try {
  //  Use XMLHttpRequest for progress support
      return new Promise<ApiResponse<T>>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
  // Progress handling
        if (config.onProgress) {
          xhr.upload.addEventListener('progress', config.onProgress);
        }
        
  // Handle response
        xhr.onload = async () => {
          try {
            const response = new Response(xhr.responseText, {
              status: xhr.status,
              statusText: xhr.statusText,
              headers: new Headers({
                'content-type': xhr.getResponseHeader('content-type') || 'application/json'
              })
            });
            
            const result = await this.handleResponse<T>(response, endpoint);
            resolve(result);
          } catch (error) {
            reject(error);
          }
        };
        
  // Error handling
        xhr.onerror = () => {
          reject(new Error('Erreur réseau lors de l\'upload'));
        };
        
        xhr.ontimeout = () => {
          reject(new Error('Timeout lors de l\'upload'));
        };
        
  // Configure request
        xhr.open('POST', url);
        xhr.timeout = config.timeout || this.timeout;
        
  // Add headers
        const headers = this.getUploadHeaders({ includeAuth, ...config });
        Object.entries(headers).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value);
        });
        
  // Send request
        xhr.send(formData);
      });
    } catch (error) {
      throw error;
    }
  }

  // ================================
  // REQUEST UTILITIES
  // ================================

  /**
   *  Build a query string from an object
   */
  buildQueryString(params: Record<string, any>): string {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(item => searchParams.append(key, String(item)));
        } else if (typeof value === 'object') {
          searchParams.append(key, JSON.stringify(value));
        } else {
          searchParams.append(key, String(value));
        }
      }
    });
    
    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : '';
  }

  /**
   *  Retry mechanism with exponential backoff
   */
  async withRetry<T>(
  operation: () => Promise<T>, 
  maxRetries = 3, 
  initialDelay = 1000
): Promise<T> {
  let lastError: any;
  let delay = initialDelay;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
  // Check a status field on any object (not only Error)
      const status = (error as any)?.status;
      if (typeof status === 'number') {
  // Client errors: do not retry
        if ([400, 401, 403, 404, 422].includes(status)) {
          throw error;
        }
      }
      
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delay));
  delay *= 2; // Exponential backoff
      }
    }
  }
  
  throw new Error(`Toutes les tentatives (${maxRetries}) ont échoué`);
  throw lastError;
}

  /**
   *  Requests with automatic retry
   */
  async getWithRetry<T>(endpoint: string, includeAuth = true, maxRetries = 3): Promise<ApiResponse<T>> {
    return this.withRetry(() => this.get<T>(endpoint, includeAuth), maxRetries);
  }

  async postWithRetry<T>(endpoint: string, body?: any, includeAuth = true, maxRetries = 3): Promise<ApiResponse<T>> {
    return this.withRetry(() => this.post<T>(endpoint, body, includeAuth), maxRetries);
  }

  // ================================
  // AUTHENTICATION MANAGEMENT
  // ================================

  /**
   *  Set the authentication token
   */
  setAuthToken(token: string, persistent = true): void {
    
    if (persistent) {
      localStorage.setItem('auth_token', token);
      localStorage.setItem('portfolio_auth_token', token);
    } else {
      sessionStorage.setItem('auth_token', token);
    }
  }

  /**
   *  Clear the authentication token
   */
  clearAuthToken(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('portfolio_auth_token');
    sessionStorage.removeItem('auth_token');
  }

  /**
   *  Decode the JWT and get user information
   */
  getUserFromToken(): any | null {
    const token = this.getAuthToken();
    if (!token) return null;

    try {
  // Validate JWT format (3 parts separated by dots)
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }

  // Decode payload part (base64url)
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      
  // Add padding if necessary
      const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
      
      const jsonPayload = decodeURIComponent(
        atob(padded)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      
      const decoded = JSON.parse(jsonPayload);
      return decoded;
    } catch (error) {
  this.clearAuthToken(); // Clean up the corrupted token
      return null;
    }
  }

  /**
   *  Validate if the token is expired
   */
  isTokenExpired(): boolean {
    const user = this.getUserFromToken();
    if (!user || !user.exp) return true;
    
    const now = Math.floor(Date.now() / 1000);
    const isExpired = user.exp < now;
    
    if (isExpired) {
      this.clearAuthToken();
    }
    
    return isExpired;
  }

  // ================================
  // CONNECTIVITY TESTS
  // ================================

  /**
   *  Validate API health
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.get('/health', false, { timeout: 5000 });
      return response.success !== false;
    } catch (error) {
      return false;
    }
  }

  /**
   *  Full connectivity test
   */
  async testConnectivity(): Promise<ConnectivityResult> {
    
    const results: ConnectivityResult = {
      api: false,
      auth: false,
      database: false,
      storage: false,
      timestamp: new Date().toISOString()
    };

  // API test
    try {
      results.api = await this.healthCheck();
    } catch (error) {
      }

  // Authentication test
    if (this.isTokenValid) {
      try {
        await this.get('/auth/me');
        results.auth = true;
      } catch (error) {
        }
    }

  // Database test
    try {
      await this.get('/projects', false, { timeout: 5000 });
      results.database = true;
    } catch (error) {
      }

  // Image storage test
    try {
      await this.get('/images/limits', false, { timeout: 5000 });
      results.storage = true;
    } catch (error) {
      }

    return results;
  }

  // ================================
  // PROPERTIES AND GETTERS
  // ================================

  /**
   *  API base URL
   */
  get apiUrl(): string {
    return this.baseURL;
  }

  /**
   *  Service version
   */
  get serviceVersion(): string {
    return this.version;
  }

  /**
   *  Validate if a token is present
   */
  get hasAuthToken(): boolean {
    return !!this.getAuthToken();
  }

  /**
   *  Validate if the token is valid
   */
  get isTokenValid(): boolean {
    return this.hasAuthToken && !this.isTokenExpired();
  }

  /**
   *  Get service information
   */
  getServiceInfo(): ServiceInfo {
    return {
      apiUrl: this.apiUrl,
      hasLocalToken: this.hasAuthToken,
      isLocalTokenValid: this.isTokenValid,
      userInfo: this.getUserFromToken(),
      timestamp: new Date().toISOString(),
      version: this.version
    };
  }

  // ================================
  // DEBUG METHODS
  // ================================

  /**
   *  Show environment information
   */
  private logEnvironmentInfo(): void {
  }

  /**
   *  Full authentication debug
   */
  debugAuth(): void {
    
    const token = this.getAuthToken();
    
    if (token) {
      
      const user = this.getUserFromToken();
      if (user) {
        console.log('User info:', {
          exp: new Date(user.exp * 1000).toLocaleString(),
          iat: new Date(user.iat * 1000).toLocaleString(),
          isExpired: this.isTokenExpired()
        });
      }
    }
    
  }

  /**
   *  Full service diagnostics
   */
  async diagnose(): Promise<void> {
    
  // Basic information
    
  // Connectivity test
    const connectivity = await this.testConnectivity();
    
  // Authentication
    this.debugAuth();
    
  }
}

// ================================
// SINGLETON INSTANCE AND EXPORT
// ================================

export const apiService = new ApiService();
export default apiService;

//  Startup log